// UploadsRow — shared контейнер model-specific UI НАД textarea.
//
// Несмотря на название, рендерит не только upload-слоты, но И audio header
// (title-поле «Название трека» + кнопка «Сгенерировать текст песни») И
// PresentationControls для Gamma (placeholder + info-card). Это всё —
// «поля настроек что мутируют при смене модели», и user-facing они должны
// быть в одном «слое» НАД textarea.
//
// Используется:
//   1) /generate и /bots/* widget → внутри GenerateInputBar.tsx,
//      заменяет inline JSX (uploads / audio-title-wrap / PresentationControls).
//   2) Home (HomeTemp.tsx) → над textarea в chat-input-wrapper,
//      чтобы юзер настраивал модель прям с главной.
//
// Self-contained: читает state из useGenerate() (App-level Provider).
// Аналог SettingsPillsRow.tsx для top-секции — single source of truth,
// новые поля добавляются ОДИН РАЗ здесь и автоматом на двух экранах.
//
// `makeShowPicker` (optional) — функция для открытия Gallery picker modal
// с предыдущими загрузками юзера. GenerateInputBar её передаёт (живёт там).
// На home передаём undefined — fallback на native file input.
//
// `appendSlot` (optional) — node, рендерится в конце ряда. Используется
// /bots/*-widget'ом для result preview-thumb справа от uploads.
import { photoSlotCapacity, photoSlotCheck, photoSlotLabel, photoSlotType, type CardPhotoSlot } from '../../../photo-slots'
import PhotoSlotHelp from './PhotoSlotHelp'
import { usePhotoSlotCheck } from '../hooks/usePhotoSlotCheck'
import { useAuth } from '../../../context/AuthContext'
import { useTranslation } from 'react-i18next'
import { namedSlotArrow, namedSlotsClassName } from '../logic/namedSlotsLayout'

import { useGenerate } from '../../../context/GenerateContext'
import { getAudioFieldAvailability } from '../utils/audioOperationAvailability'
import { Icon } from '../../../../public/icons/Icon.tsx'

import UploadThumb from './UploadThumb'
import ScenePickerHost from './ScenePicker'
import { getScene, sceneAssetOfUrl, useScene } from '../../../api/sceneSelection'
import { useSceneApply } from '../hooks/useSceneApply'
import { assetIdsInPrompt, assetToken, stripAssetTokens } from '../../../prompt-vars/phrases'
import MultiImgRow from './MultiImgRow'
import MultiVideoRow from './MultiVideoRow'
import { acceptFor, isSeedance25Model, slotHint, videoIntentOf, videoIntentProblem } from '../utils/referenceLimits'
import { UPLOAD_SIZE_MB } from '../hooks/useGenerateUploads'
import MultiAudioRow from './MultiAudioRow'
import AudioTrimSheet from './AudioTrimSheet'
import VideoTrimSheet from './VideoTrimSheet'
import { Fragment, useEffect, useRef, useState } from 'react'
import TextInputBadge from './TextInputBadge'
import SunoLyricsButton from './SunoLyricsButton'
import PresentationControls from './PresentationControls'
import SceneExtendPin from './SceneExtendPin'
import type { UploadedFile } from '../utils/uploadHelpers'
import { hasFrameRefModes } from '../../../utils/frameRefModes'

interface UploadsRowProps {
	/** Опционально: фабрика «открыть gallery picker» для button'а. Если не
	 *  задана, UploadThumb открывает native file input. */
	makeShowPicker?: (
		type: 'images' | 'videos' | 'audios',
		ref: React.RefObject<HTMLInputElement | null>,
		videoTarget?: 'uploadedVideo' | 'referenceVideo' | 'referenceVideos',
		imageTarget?: 'uploadedImage' | 'swapImage' | 'endImage' | 'slot',
		/** Индекс именованного фото-слота карточки при imageTarget === 'slot'. */
		slotIndex?: number,
	) => (buttonEl: HTMLElement) => void
	/** Опционально: дополнительный node в конце (для result preview в widget'е). */
	appendSlot?: React.ReactNode
	/** Опционально: tooltip-функция (для override DEFAULT_HINTS на model.paramHints).
	 *  Если не задана, использует встроенные DEFAULT_HINTS из этого файла. */
	hint?: (key: string) => string | undefined
	/** Опционально: callback для открытия preview модалки результата
	 *  презентации (используется в PresentationControls.PresentationResultSlot).
	 *  На /generate это открывает CardModal. На home не нужен. */
	onOpenResult?: () => void
	/** Опционально (home): не показывать инфо-блок презентаций в create-режиме —
	 *  на home все настройки Gamma уже в пилюлях, плейсхолдер с подсказкой лишний.
	 *  Раньше проп ещё прятал тумблер Create/Edit; тумблер убран (10.09). */
	hidePresentationInfo?: boolean
	/** Опционально: скрыть ВСЕ видео-вводы (слот «Видео движения» + референс-
	 *  каналы single/multi). Tool-режим (GenerateToolMode) прячет их, когда
	 *  админ задал видео-референс карточки с локом — генерация всегда идёт
	 *  с референсом «под капотом». */
	/** Раскладка для моделей, работающих ТОЛЬКО с фото (noPrompt + два снимка):
	 *  два именованных слота в порядке, понятном человеку. Порядок ОТПРАВКИ при
	 *  этом прежний — см. комментарий у самих слотов. */
	faceSwapLayout?: boolean
	/** Скрыть первый фото-слот: его заполняет админский референс карточки. */
	hideImageSlot?: boolean
	hideVideoSlot?: boolean
	/** Опционально: скрыть аудио-вводы (referenceAudios) — тот же лок, но для
	 *  аудио-референса карточки. */
	hideAudioSlot?: boolean
	/** Опционально: скелет-плитки пустых слотов мульти-рядов до лимита модели
	 *  (tool-режим: юзер видит, сколько файлов можно добавить). */
	slotSkeletons?: boolean
	/** Именованные фото-слоты карточки (владелец 2026-08-14): подписи ячеек
	 *  задаёт админ, фото уходят referenceImages-массивом В ПОРЯДКЕ слотов —
	 *  промпт карточки командует «первым/вторым фото». Требует модель с
	 *  мульти-фото входом; активен при length >= 2. */
	namedSlots?: CardPhotoSlot[]
	/** Мультистиль: номера слотов, которые использует ВЫБРАННЫЙ стиль
	 *  ({photoN} в его промпте) — рендерятся и требуются только они;
	 *  пустой массив = стиль слоты не использует (обычные входы модели);
	 *  null/undefined — фильтра нет (все слоты). */
	activeSlotNums?: number[] | null
	/** Виджет карточки (владелец 2026-08-16): скрыть капсулу «Персонаж». */
	hideCharacter?: boolean
	/** Лимит фото юзера в мульти-входе (кламп по модели). */
	photoLimit?: number | null
	/** Подпись под слотом «Ваше фото» (описание, как у именованных слотов). */
	photoHint?: string
	/** Видео-поля карточки (владелец 2026-08-20): скрыть слот «Последний кадр»
	 *  / подпись под ним; подпись под капсулой «Персонаж». */
	hideEndImage?: boolean
	endImageHint?: string
	characterHint?: string
}

/** Ячейка именованного фото-слота: свой inputRef на каждый слот (UploadThumb
 *  требует ref), подпись + подсказка от админа. Тип поля (18.10, photo-slots) добавляет
 *  ВНУТРЬ плитки значок в углу, «?» с листом «что загрузить» и, если админ включил на
 *  поле проверку, полосу ответа сервера после загрузки («лицо найдено»). Вокруг плитки
 *  ничего нового (владелец: «просто улучшить слоты»). */
function NamedSlotCell({ slot, index, file, isUploading, isLoading, onUpload, onRemove, makeShowPicker, tooltip }: {
	slot: CardPhotoSlot
	index: number
	/** Требования модели к фото (utils/referenceLimits.slotHint) — подсказка пустого слота. */
	tooltip?: string
	file: UploadedFile | null
	isUploading: boolean
	isLoading: boolean
	onUpload: (idx: number, e: React.ChangeEvent<HTMLInputElement>) => void
	onRemove: (idx: number) => void
	/** Пикер источника «Загрузить / Из моих генераций» — стандарт слотов проекта (владелец, 25.09);
	 *  галерея кладёт файл в ЭТОТ слот (цель 'slot' + индекс), канал отправки не меняется. */
	makeShowPicker?: UploadsRowProps['makeShowPicker']
}) {
	const inputRef = useRef<HTMLInputElement>(null)
	// Якорь поповера «?» — элемент кнопки из обработчика клика (state, не ref: ref нельзя читать при рендере).
	const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null)
	const helpOpen = !!helpAnchor
	const type = photoSlotType(slot.type)
	const label = photoSlotLabel(slot, index + 1)
	// Проверка идёт по загруженному файлу (url появляется после загрузки), только если админ включил её на поле и
	// только у вошедшего: ручка под токеном, а 401 гостя увёл бы на /login (правило 08.10) — фото гостя не проверяем.
	const { user } = useAuth()
	const check = usePhotoSlotCheck(user ? photoSlotCheck(slot) : null, file?.url)
	const hasHelp = !!(type || slot.hint)
	return (
		<div className="gm__faceswap-slot">
			<div className={`gm__nslot${type ? ' gm__nslot--typed' : ''}${hasHelp ? ' gm__nslot--help' : ''}${check ? ' gm__nslot--checked' : ''}`}>
				<UploadThumb
					file={file}
					isUploading={isUploading}
					isLoading={isLoading}
					accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
					inputRef={inputRef}
					onChange={(e) => onUpload(index, e)}
					onRemove={() => onRemove(index)}
					// Пикер собираем в момент клика: ref попадает в фабрику из обработчика, а не при рендере (react-hooks/refs).
					onShowPicker={makeShowPicker ? (btn) => makeShowPicker('images', inputRef, undefined, 'slot', index)(btn) : undefined}
					icon={<Icon id="gen-add" size={20} />}
					label={label}
					tooltip={tooltip}
					required
				/>
				{type && !file && (
					<span className="gm__nslot-mark" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
							<path d={type.icon} />
						</svg>
					</span>
				)}
				{hasHelp && !file && (
					<button
						type="button"
						className="gm__nslot-help"
						aria-label={`Что загрузить: ${label}`}
						aria-expanded={helpOpen}
						onClick={(e) => {
							e.stopPropagation()
							setHelpAnchor(e.currentTarget)
						}}
					>
						?
					</button>
				)}
				{check && (
					<span className={`gm__nslot-check gm__nslot-check--${check.status}`} title={check.note} role="status">
						{check.text}
					</span>
				)}
			</div>
			{slot.hint && <span className="gm__faceswap-hint">{slot.hint}</span>}
			{hasHelp && <PhotoSlotHelp open={helpOpen} onClose={() => setHelpAnchor(null)} anchorEl={helpAnchor} slot={slot} label={label} />}
		</div>
	)
}

const DEFAULT_HINTS: Record<string, string> = {
	image:
		'Референсное изображение. Аватары, фон и другие элементы создаются на основе этого изображения.',
	video:
		'Референсное видео. Движения аватара повторяют движения из этого видео. Поддерживаются .mp4/.mov, макс. 100 МБ, длительность 3–30 сек.',
	swap_image: 'Изображение лица для замены. Используйте чёткое фото лица анфас без очков.',
	end_image:
		'Изображение последнего кадра. Модель плавно перейдёт от первого кадра к этому изображению.',
	reference_video:
		'Видео-референс для стиля съёмки или редактирования. Поддерживаются .mp4/.mov.',
	reference_images:
		'Референсные изображения для стиля или аватаров. Модель учтёт их при генерации.',
	reference_videos:
		'До 3 видео-референсов для стиля и движения. Поддерживаются .mp4/.mov/.webm, макс. 100 МБ каждое.',
	reference_audios:
		'До 3 аудио-референсов (wav, mp3, aac, m4a, ogg). Макс. 15 МБ каждый.',
}

export default function UploadsRow({
	makeShowPicker,
	appendSlot,
	hint: hintProp,
	onOpenResult,
	hidePresentationInfo,
	faceSwapLayout,
	hideImageSlot,
	hideVideoSlot,
	hideAudioSlot,
	slotSkeletons,
	namedSlots,
	activeSlotNums,
	photoLimit,
	photoHint,
	hideEndImage,
	endImageHint,
}: UploadsRowProps = {}) {
	const { t } = useTranslation()
	const gen = useGenerate()
	const {
		selectedModel: model,
		isLoading,
		isUploading,
		isAudio,
		isPresentation,
		requiresImage,
		requiresVideo,
		requiresTwoImages,
		supportsMultipleImages,
		uploadedImage,
		swapImage,
		uploadedVideo,
		endImage,
		referenceVideo,
		referenceVideos,
		referenceAudios,
		referenceImages,
		omniReferenceImages,
		fileInputRef,
		swapInputRef,
		videoInputRef,
		endImageInputRef,
		refImagesInputRef,
		refVideoInputRef,
		refVideosInputRef,
		refAudiosInputRef,
		handleImageUpload,
		handleMultipleImageUpload,
		slotImages,
		handleSlotImageUpload,
		removeSlotImage,
		setActiveSlotNums,
		handleSwapImageUpload,
		handleVideoUpload,
		handleEndImageUpload,
		handleOmniRefImageUpload,
		handleReferenceVideoUpload,
		handleReferenceVideosUpload,
		handleReferenceAudiosUpload,
		audioTrimFile,
		applyTrimmedAudio,
		cancelAudioTrim,
		videoTrimUrl,
		applyTrimmedVideo,
		cancelVideoTrim,
		setUploadedImage,
		setSwapImage,
		setUploadedVideo,
		setEndImage,
		setReferenceImages,
		setOmniReferenceImages,
		setReferenceVideo,
		setReferenceVideos,
		setReferenceAudios,
		dragProps,
		showStyleHint,
		setShowStyleHint,
		// Audio-specific (Suno) — title + lyrics-generator UI. Настройки здесь меняет только человек — с отметкой
		// «изменил пользователь» (журнал генерации в админке).
		settings,
		userUpdateSetting: updateSetting,
		setPrompt,
	} = gen

	// Сцена (персонажи, локации, предметы): выбор — пикер сцены (пункты меню фото-слота), применение —
	// useSceneApply ниже (план набора с бэка → существующие каналы). UploadsRow общий для бара и вида карточки →
	// хост и отметки живут в обоих без дублирования.
	const scene = useScene()

	// Метка «фото ассета сцены» на тумбе: кольцо (сверка по path — подпись presigned меняется, путь стабилен).
	// undefined = обычная тумба.
	const charMarkFor = (url?: string | null) => {
		const a = sceneAssetOfUrl(scene, url)
		return a ? { avatar: a.avatarUrl, letter: a.name.slice(0, 1).toUpperCase(), blocked: !!scene?.blocked } : undefined
	}
	const charMark = scene?.placedUrls.length
		? { avatar: null, letter: '', blocked: scene.blocked }
		: undefined

	// Пары {slot, idx}: idx — ИСХОДНЫЙ номер слота (позиция = смысл, файлы
	// живут в slotImages[idx]); при фильтре активного стиля рендерится только
	// его набор, а нумерация фраз «the first/second image» пересчитывается
	// рангом в substitutePhotoSlots — файлы и промпт не разъезжаются.
	// ЖИВЁТ ДО гарда `!model` — хуки должны зваться безусловно.
	// Ёмкость под именованные слоты: у фото-моделей — maxImages; у Seedance-2
	// фото-слоты уходят референсами (режим «Референсы», до 9) — владелец
	// 2026-08-20: слоты карточек работают и для видео. Правило общее с конструктором
	// (photo-slots/photoSlotCapacity) и с GenerateToolMode.
	const slotCapacity = photoSlotCapacity(model)
	const slotPairs =
		namedSlots && namedSlots.length >= 2 && slotCapacity >= namedSlots.length && !requiresTwoImages
			? namedSlots
					.map((slot, idx) => ({ slot, idx }))
					.filter(({ idx }) => activeSlotNums == null || activeSlotNums.includes(idx + 1))
			: null
	const activeNamedSlots = slotPairs && slotPairs.length > 0 ? slotPairs : null

	// Сообщаем хуку активный набор: зеркало referenceImages отдаёт в payload
	// только слоты выбранного стиля (файл «чужого» слота не утекает).
	useEffect(() => {
		if (!namedSlots || namedSlots.length < 2) {
			setActiveSlotNums(null)
			return
		}
		setActiveSlotNums(activeNamedSlots ? activeNamedSlots.map(({ idx }) => idx + 1) : [])
	}, [setActiveSlotNums, namedSlots, activeNamedSlots])

	// Применение сцены. Пределы — те же, что у ряда фото: лимит карточки и место под её именованные слоты
	// (фото сцены встают вслед за ними и чужое место не занимают).
	const { applyScene, probePlan } = useSceneApply({ photoLimit, reservedSlots: activeNamedSlots?.length ?? 0 })

	// «Готово» в пикере: новый набор сцены. Чипы новых ассетов — в конец промпта (как «Use in Project» у
	// Artlist), чипы снятых — из промпта; дальше план и раскладка фото.
	const confirmScene = (ids: string[], prefer?: 'sheet' | 'photos') => {
		const before = getScene()?.assets.map((a) => a.id) ?? []
		const removed = before.filter((id) => !ids.includes(id))
		// Текст промпта — из контекста (голое `prompt` здесь — глобальная window.prompt, TS её пропускает).
		const current = gen.prompt
		let next = removed.length ? stripAssetTokens(current, removed) : current
		const mentioned = assetIdsInPrompt(next)
		const added = ids.filter((id) => !mentioned.includes(id))
		if (added.length) next = `${next.trimEnd()}${next.trim() ? ' ' : ''}${added.map(assetToken).join(' ')} `
		if (next !== current) setPrompt(next)
		// Явный выбор: фото ассета встаёт и в единственный слот, даже если там фото юзера (оно вернётся при снятии).
		void applyScene(ids, { prefer, replace: true })
	}

	if (!model) return null

	// Матрица доступности audio-полей (зависит от audio operation: extend/cover/
	// add_vocals/...). Нужна для disabled-состояний SunoLyricsButton и других
	// audio-pills. На home audioOperation всегда дефолтный 'generate', но
	// гет-функция всё равно отдаёт корректную матрицу.
	const audioAvail = getAudioFieldAvailability(gen.audioOperation)

	const maxImages = model.maxImages ?? 1
	// Лимит фото карточки: инструмент часто заточен под 1-2 снимка — админ
	// режет мульти-вход, не трогая модель (кламп по модельному максимуму).
	const effMaxImages = photoLimit ? Math.max(1, Math.min(maxImages, photoLimit)) : maxImages
	const cappedMultiUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (photoLimit && e.target.files) {
			const room = Math.max(0, effMaxImages - referenceImages.length)
			if (e.target.files.length > room) {
				const dt = new DataTransfer()
				Array.from(e.target.files).slice(0, room).forEach((f) => dt.items.add(f))
				e.target.files = dt.files
			}
		}
		handleMultipleImageUpload(e)
	}
	// Лимит «1 фото» (владелец 2026-08-17): мульти-ряд с единственной пилюлей
	// сбивает с толку — рендерим обычный одиночный слот, фото ложится ПРЯМО в
	// него, новое — заменяет. Канал прежний (referenceImages[0]), payload
	// мульти-модели не меняется.
	const singleRefMode =
		effMaxImages === 1 && supportsMultipleImages && !requiresImage && !requiresTwoImages
	const singleRefUpload = (
		e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } },
	) => {
		if (!e.target.files?.length) return
		setReferenceImages(() => [])
		handleMultipleImageUpload(e as React.ChangeEvent<HTMLInputElement>)
	}
	// Слот-режим жив только у мульти-фото модели и в пределах её лимита:
	// у одно-входной модели «второе фото» просто не доедет до payload.
	const showImageUpload =
		requiresImage || model.supportsImageInput || requiresTwoImages || supportsMultipleImages

	// Резолв tooltip — приоритет: проп hint (для admin paramHints override),
	// иначе DEFAULT_HINTS словарь.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const paramHints = (model as Record<string, any>).paramHints || {}
	// Приоритет: ручная подсказка админа → лимиты из кода модели (форматы, секунды,
	// размер: utils/referenceLimits.slotHint) → общий текст компонента.
	// У фото требования модели (форматы, стороны, пропорции — по ним фото и подгоняется при загрузке) не
	// прячутся за текстом админа, а идут следом: клиент сразу видит, что грузить (владелец 29.09).
	const IMAGE_KEYS = ['image', 'reference_images', 'end_image']
	const hint = hintProp ?? ((key: string): string | undefined => {
		const own = paramHints[key]?.description
		const rules = slotHint(key, model, UPLOAD_SIZE_MB)
		if (own && rules && IMAGE_KEYS.includes(key)) return `${own} ${rules}`
		return own || rules || DEFAULT_HINTS[key]
	})

	// ── Presentation: PresentationControls содержит:
	//      • PresentationResultSlot (placeholder + click-to-preview)
	//      • GammaCreateHint (чистый info-block для create-mode) ИЛИ
	//        UmnikUploadZone (file-upload для edit-mode, interactive)
	//      • ModeToggle (Create/Edit — но втихую меняет model)
	//
	// На home (hidePresentationInfo=true) интерфейс должен показывать
	// ТОЛЬКО interactive поля. PresentationPills (внизу через
	// SettingsPillsRow) уже покрывают: Тема, Слайдов, Язык, AI-генерация,
	// Аудитория, Инструкции — это и есть все настройки результата для
	// Gamma create-mode. PresentationControls в create-mode становится
	// pure info-block (placeholder + hint без UI-управления) — на home
	// он лишний.
	//
	// Для edit-mode (umnik-slides, модель скрыта в админке — режим возможен
	// только через «Воссоздать» старой правки) PresentationControls несёт
	// interactive UmnikUploadZone (загрузка .pptx) — её на home надо
	// показывать. Поэтому проверяем mode, а не просто isHome.
	if (isPresentation) {
		const presMode = settings.presentation?.mode ?? 'create'
		const skipInfoBlock = hidePresentationInfo && presMode === 'create'
		if (skipInfoBlock) return null
		return <PresentationControls onOpenResult={onOpenResult} />
	}

	// ── Audio (Suno sounds-режим): BPM + тональность (опциональные
	//    параметры для SFX-генерации). + appendSlot (result-preview) справа
	//    через flex space-between из .gm__uploads--audio wrapper родителя.
	if (isAudio && settings.audioSubmode === 'sounds') {
		return (
			<>
				<div className="suno-sounds-params">
					<div className="suno-sounds-params__row">
						<label className="suno-sounds-params__label" htmlFor="sound-tempo-h">
							BPM
						</label>
						<input
							id="sound-tempo-h"
							className="suno-sounds-params__input"
							type="number"
							min={1}
							max={300}
							placeholder="авто"
							value={settings.soundTempo ?? ''}
							onChange={(e) => {
								const n = Number(e.target.value)
								updateSetting('soundTempo', Number.isFinite(n) && n > 0 ? n : undefined)
							}}
							disabled={isLoading}
						/>
					</div>
					<div className="suno-sounds-params__row">
						<label className="suno-sounds-params__label" htmlFor="sound-key-h">
							Тональность
						</label>
						<input
							id="sound-key-h"
							className="suno-sounds-params__input"
							type="text"
							placeholder="Any, Cm, D, F#m..."
							value={settings.soundKey ?? ''}
							onChange={(e) => updateSetting('soundKey', e.target.value || undefined)}
							disabled={isLoading}
						/>
					</div>
				</div>
				{appendSlot}
			</>
		)
	}

	// ── Audio (Suno music/lyrics-режим): поле «Название трека» +
	//    кнопка «Сгенерировать текст песни» (открывает модалку, при
	//    «Использовать» обновляет audioTitle + prompt). autoResize у
	//    textarea не вызываем — на home textarea fixed-rows, на /generate
	//    GenerateInputBar.textareaRef autoResize вызовется при следующем
	//    setPrompt-driven re-render (см. autoResize useEffect в GenerateInputBar).
	if (isAudio) {
		// Озвучка (TTS): только результат-слот, без лирик-хедера и загрузок.
		if (settings.audioSubmode === 'tts') {
			return <>{appendSlot}</>
		}
		// Soundtrack (Mureka): фото→музыка. Вместо лирик-хедера — слот загрузки фото.
		if (settings.audioSubmode === 'soundtrack') {
			return (
				<>
					<UploadThumb
						file={uploadedImage}
						characterMark={charMarkFor(uploadedImage?.url)}
						isUploading={isUploading}
						isLoading={isLoading}
						accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
						inputRef={fileInputRef}
						onChange={handleImageUpload}
						onRemove={() => {
							setUploadedImage(null)
							if (fileInputRef.current) fileInputRef.current.value = ''
						}}
						dragProps={dragProps(handleImageUpload)}
						icon={<Icon id="gen-add" size={20} />}
						label="Фото для саундтрека"
						tooltip="Загрузите фото — Mureka создаст музыку под него."
					/>
					{appendSlot}
				</>
			)
		}
		return (
			<>
				<div className="gm__audio-title-wrap">
					<div
						className={`gm__field-outlined gm__field-outlined--compact${
							settings.audioTitle ? ' gm__field-outlined--filled' : ''
						}`}
					>
						<input
							type="text"
							className="gm__field-outlined__input"
							value={settings.audioTitle || ''}
							onChange={(e) => updateSetting('audioTitle', e.target.value)}
							maxLength={100}
							disabled={isLoading}
							id="audio-title-input-shared"
							placeholder=" "
						/>
						<fieldset className="gm__field-outlined__fieldset" aria-hidden="true">
							<legend className="gm__field-outlined__legend">
								<span>{t('generate.audio.trackTitle')}</span>
							</legend>
						</fieldset>
						<label htmlFor="audio-title-input-shared" className="gm__field-outlined__label">
							{t('generate.audio.trackTitle')}
						</label>
					</div>
					<span title={!audioAvail.lyrics.enabled ? audioAvail.lyrics.reason : undefined}>
						<SunoLyricsButton
							model={model}
							currentTitle={settings.audioTitle || ''}
							onApply={({ title, lyrics }) => {
								if (title) updateSetting('audioTitle', title)
								setPrompt(lyrics)
							}}
							disabled={isLoading || !audioAvail.lyrics.enabled}
						/>
					</span>
				</div>
				{appendSlot}
			</>
		)
	}

	const frameRefModes = hasFrameRefModes(model)
	const seedanceMode = settings.seedanceMode ?? 'frames'

	// Whether anything renders. Если нет — родитель может скрыть весь .gm__uploads.
	const anyUploadVisible =
		showImageUpload ||
		requiresVideo ||
		model?.supportsReferenceVideo ||
		model?.supportsReferenceAudio ||
		model?.supportsEndImage ||
		model?.supportsReferenceImages ||
		!!appendSlot

	if (!anyUploadVisible) {
		// Single «T» badge для pure text-моделей когда есть appendSlot
		// (используется в /bots/* widget'е для рендера result-preview).
		return appendSlot ? (
			<>
				<TextInputBadge taskType={model.taskType} />
				{appendSlot}
			</>
		) : null
	}

	// React-hooks/refs warning подавлен — мы НЕ читаем .current во время
	// render, только передаём ref forward в makeShowPicker, который сам
	// читает .current в event handler (click). Это безопасный pattern
	// идентичный originalу в GenerateInputBar.tsx до экстракта.
	/* eslint-disable react-hooks/refs */
	const pickerImages = makeShowPicker ? makeShowPicker('images', fileInputRef) : undefined
	// Явно указываем канал: без него выбор из галереи уходил в uploadedImage,
	// и фото со слота «Ваше фото» приземлялось в «картинку-основу».
	const pickerSwap = makeShowPicker
		? makeShowPicker('images', swapInputRef, undefined, 'swapImage')
		: undefined
	const pickerVideo = makeShowPicker ? makeShowPicker('videos', videoInputRef, 'uploadedVideo') : undefined
	const pickerRefVideo = makeShowPicker ? makeShowPicker('videos', refVideoInputRef, 'referenceVideo') : undefined
	const pickerRefImages = makeShowPicker ? makeShowPicker('images', refImagesInputRef) : undefined
	// Последний кадр — тот же пикер (Загрузить / Из моих генераций / Персонаж), что у остальных фото-слотов (владелец 23.08)
	const pickerEndImage = makeShowPicker ? makeShowPicker('images', endImageInputRef, undefined, 'endImage') : undefined
	const pickerRefVideos = makeShowPicker ? makeShowPicker('videos', refVideosInputRef, 'referenceVideos') : undefined
	const pickerRefAudios = makeShowPicker ? makeShowPicker('audios', refAudiosInputRef) : undefined
	/* eslint-enable react-hooks/refs */

	// Одиночный слот лимита «1 фото» — встаёт на место MultiImgRow (см.
	// singleRefMode выше); null, когда режим не активен.
	const singleRefThumb = singleRefMode ? (
		<UploadThumb
			file={referenceImages[0] ?? null}
			characterMark={charMarkFor(referenceImages[0]?.url)}
			isUploading={isUploading}
			isLoading={isLoading}
			accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
			inputRef={fileInputRef}
			onChange={singleRefUpload}
			onRemove={() => {
				setReferenceImages(() => [])
				if (fileInputRef.current) fileInputRef.current.value = ''
			}}
			dragProps={dragProps(singleRefUpload)}
			icon={<Icon id="gen-add" size={20} />}
			label={t('generate.upload.addImage')}
			tooltip={hint('image')}
			onHintDismiss={() => setShowStyleHint(false)}
			onShowPicker={pickerImages}
		/>
	) : null

	return (
		<>
			{/* Продление сцены Omni: контекст «к чему приклеится результат».
			    Не слот — источник это прошлая генерация, а не файл. Компонент сам
			    решает, показываться ли (нет источника или модель без продления —
			    рисует null). */}
			<SceneExtendPin />
			{/* Модель работает только с фото: два именованных слота «своё → куда».
			    ВАЖНО про порядок. Человеку естественно начать с себя, а модель ждёт
			    ПЕРВОЙ целевую картинку (input_image) и ВТОРЫМ лицо (swap_image).
			    Поэтому визуальный порядок обратный порядку отправки: слот «Ваше
			    фото» пишет в swapImage, слот «куда перенести» — в uploadedImage.
			    Сборка payload остаётся прежней ([uploadedImage, swapImage]), так что
			    результат правильный, а подписи — человеческие. */}
			{/* Именованные фото-слоты карточки: ячейка на слот, подписи админа.
			    Файлы живут в gen.slotImages (позиция = смысл), referenceImages
			    зеркалит их плотно — сборка payload прежняя (мульти-канал). */}
			{activeNamedSlots ? (
				// Раскладка по числу полей (владелец 28.09, стили — _tool-mode.scss): 2 — подписи по бокам на мобиле,
				// 3 — в ряд с подписями под полями, 4 — сеткой 2×2 (две пары; стрелка между парами не нужна).
				// От пяти (18.10) — сетка без стрелок, переносится по ширине зоны сама: logic/namedSlotsLayout.
				<div className={namedSlotsClassName(activeNamedSlots.length)}>
					{activeNamedSlots.map(({ slot, idx }, i) => (
						<Fragment key={idx}>
							{namedSlotArrow(activeNamedSlots.length, i) && (
								<span
									className={`gm__faceswap-arrow${namedSlotArrow(activeNamedSlots.length, i) === 'pair-break' ? ' gm__faceswap-arrow--pair-break' : ''}`}
									aria-hidden="true"
								>
									→
								</span>
							)}
							<NamedSlotCell
								slot={slot}
								index={idx}
								file={slotImages[idx] ?? null}
								isUploading={isUploading}
								isLoading={isLoading}
								onUpload={handleSlotImageUpload}
								onRemove={removeSlotImage}
								makeShowPicker={makeShowPicker}
								tooltip={slotHint('image', model, UPLOAD_SIZE_MB)}
							/>
						</Fragment>
					))}
				</div>
			) : faceSwapLayout && requiresTwoImages ? (
				<>
				<div className="gm__faceswap">
					<div className="gm__faceswap-slot">
						<UploadThumb
							file={swapImage}
							isUploading={isUploading}
							isLoading={isLoading}
							accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
							inputRef={swapInputRef}
							onChange={handleSwapImageUpload}
							onRemove={() => setSwapImage(null)}
							dragProps={dragProps(handleSwapImageUpload)}
							icon={<Icon id="gen-swap" size={20} />}
							label="Ваше фото"
							required
							onShowPicker={pickerSwap}
						/>
						<span className="gm__faceswap-hint">лицо, которое перенесём</span>
					</div>
					<span className="gm__faceswap-arrow" aria-hidden="true">→</span>
					{!hideImageSlot && (
						<div className="gm__faceswap-slot">
							<UploadThumb
								file={uploadedImage}
								characterMark={charMarkFor(uploadedImage?.url)}
								isUploading={isUploading}
								isLoading={isLoading}
								accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
								inputRef={fileInputRef}
								onChange={handleImageUpload}
								onRemove={() => {
									setUploadedImage(null)
									if (fileInputRef.current) fileInputRef.current.value = ''
								}}
								dragProps={dragProps(handleImageUpload)}
								icon={<Icon id="gen-add" size={20} />}
								label="Куда перенести"
								required
								onShowPicker={pickerImages}
							/>
							<span className="gm__faceswap-hint">картинка-основа</span>
						</div>
					)}
				</div>
				{/* Требование ко входу, о котором модель молчит: не найдя лица, она
				    возвращает картинку БЕЗ изменений — человек видит «ничего не
				    произошло» и списанные токены. Пишем до генерации, а не после. */}
				<p className="gm__faceswap-note">
					{hideImageSlot ? (
						// Картинку-основу подложил админ, юзер её не видит и не меняет —
						// говорить «на обоих фото» значит требовать невозможного.
						<>
							Лицо на вашем фото — <b>крупно и открыто</b>. Иначе модель его не найдёт
							и вернёт картинку без изменений.
						</>
					) : (
						<>
							На обоих фото лицо — <b>крупно и открыто</b>. Иначе модель его не найдёт
							и вернёт картинку без изменений.
						</>
					)}
				</p>
				</>
			) : (
			<>
			{/* hideImageSlot: первый слот заполняет админский референс карточки —
			    юзеру он не нужен. Второй слот (его собственное фото) ниже остаётся. */}
			{showImageUpload && !hideImageSlot &&
				(frameRefModes
					? seedanceMode === 'frames'
						? (
							<UploadThumb
								file={uploadedImage}
								characterMark={charMarkFor(uploadedImage?.url)}
								isUploading={isUploading}
								isLoading={isLoading}
								accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
								inputRef={fileInputRef}
								onChange={handleImageUpload}
								onRemove={() => {
									setUploadedImage(null)
									if (fileInputRef.current) fileInputRef.current.value = ''
								}}
								dragProps={dragProps(handleImageUpload)}
								icon={<Icon id="gen-add" size={20} />}
								label={t('generate.upload.addImage')}
								tooltip={'Загрузите начальный кадр.'}
								onShowPicker={pickerImages}
							/>
						)
						: (
							singleRefThumb ?? (
								<MultiImgRow
									images={referenceImages}
									characterMark={charMark}
									markedUrls={scene?.placedUrls}
									maxImages={effMaxImages}
									isLoading={isLoading}
									isUploading={isUploading}
									inputRef={fileInputRef}
									onUpload={cappedMultiUpload}
									onRemove={(idx) => setReferenceImages((prev) => prev.filter((_, i) => i !== idx))}
									tooltip={`До ${effMaxImages} фото для стиля и аватаров.`}
									onShowPicker={pickerImages}
									slotSkeletons={slotSkeletons}
								/>
							)
						)
					: supportsMultipleImages && !requiresImage && !requiresTwoImages ? (
					singleRefThumb ?? (
						<MultiImgRow
							images={referenceImages}
							characterMark={charMark}
							markedUrls={scene?.placedUrls}
							maxImages={effMaxImages}
							isLoading={isLoading}
							isUploading={isUploading}
							inputRef={fileInputRef}
							onUpload={cappedMultiUpload}
							onRemove={(idx) => setReferenceImages((prev) => prev.filter((_, i) => i !== idx))}
							tooltip={hint('image')}
							onHintDismiss={() => setShowStyleHint(false)}
							onShowPicker={pickerImages}
							slotSkeletons={slotSkeletons}
						/>
					)
				) : (
					<UploadThumb
						file={uploadedImage}
						characterMark={charMarkFor(uploadedImage?.url)}
						isUploading={isUploading}
						isLoading={isLoading}
						accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
						inputRef={fileInputRef}
						onChange={handleImageUpload}
						onRemove={() => {
							setUploadedImage(null)
							if (fileInputRef.current) fileInputRef.current.value = ''
						}}
						dragProps={dragProps(handleImageUpload)}
						icon={<Icon id="gen-add" size={20} />}
						label={
							// У модели-замены лица «исходное изображение» читается двояко:
							// человек грузит туда своё лицо, хотя это целевая картинка.
							// Подписываем ролями явно — порядок слотов тут смыслонесущий.
							requiresTwoImages
								? t('generate.upload.faceSwapTarget')
								: requiresImage
								? t('generate.upload.sourceImage')
								: t('generate.upload.addImage')
						}
						required={requiresImage || requiresTwoImages}
						tooltip={hint('image')}
						showHint={showStyleHint && !uploadedImage}
						onHintDismiss={() => setShowStyleHint(false)}
						onShowPicker={pickerImages}
					/>
				))}

			{/* Админская подпись под слотом «Ваше фото» (владелец 2026-08-16) —
			    описание входа, как hint у именованных слотов. */}
			{photoHint && showImageUpload && !hideImageSlot && (
				<span className="gm__faceswap-hint gm__photo-hint">{photoHint}</span>
			)}

			{requiresTwoImages && (
				<UploadThumb
					file={swapImage}
					isUploading={isUploading}
					isLoading={isLoading}
					accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
					inputRef={swapInputRef}
					onChange={handleSwapImageUpload}
					onRemove={() => setSwapImage(null)}
					dragProps={dragProps(handleSwapImageUpload)}
					icon={<Icon id="gen-swap" size={20} />}
					label={t('generate.upload.faceSwapSource')}
					required
					onShowPicker={pickerSwap}
				/>
			)}

			{requiresVideo && !hideVideoSlot && (
				<UploadThumb
					file={uploadedVideo}
					isUploading={isUploading}
					isLoading={isLoading}
					accept="video/mp4,video/quicktime,video/webm"
					inputRef={videoInputRef}
					onChange={handleVideoUpload}
					onRemove={() => setUploadedVideo(null)}
					dragProps={dragProps(handleVideoUpload)}
					icon={<Icon id="gen-video" size={20} />}
					label={t('generate.upload.motionVideo')}
					tooltip={hint('video')}
					required
					onShowPicker={pickerVideo}
				/>
			)}

			{/* «Последний кадр» показываем, как только в фото-канале что-то есть.
			    Раньше условием был только uploadedImage — одиночный слот. У модели
			    с мультизагрузкой (maxImages > 1) он всегда пуст, фото лежат в
			    referenceImages, и слот не появлялся вовсе: у Gemini Omni (Original)
			    кадры так и остались бы недоступны. */}
			{(model.supportsEndImage || (frameRefModes && seedanceMode === 'frames')) && (uploadedImage || referenceImages.length > 0) && !hideEndImage && (
				<UploadThumb
					file={endImage}
					isUploading={isUploading}
					isLoading={isLoading}
					accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
					inputRef={endImageInputRef}
					onChange={handleEndImageUpload}
					onRemove={() => setEndImage(null)}
					dragProps={dragProps(handleEndImageUpload)}
					icon={<Icon id="gen-add" size={20} />}
					label={t('generate.upload.endFrame')}
					onShowPicker={pickerEndImage}
					tooltip={
						endImageHint
							|| (frameRefModes
								? 'Последний кадр — необязателен.'
								: hint('end_image'))
					}
				/>
			)}

			{model?.supportsReferenceVideo && (model?.maxReferenceVideos ?? 1) <= 1 && !hideVideoSlot && (
				<UploadThumb
					file={referenceVideo}
					isUploading={isUploading}
					isLoading={isLoading}
					accept="video/mp4,video/quicktime,video/webm"
					inputRef={refVideoInputRef}
					onChange={handleReferenceVideoUpload}
					onRemove={() => setReferenceVideo(null)}
					dragProps={dragProps(handleReferenceVideoUpload)}
					icon={<Icon id="gen-video" size={20} />}
					label={t('generate.upload.videoReference')}
					tooltip={hint('reference_video')}
					onShowPicker={pickerRefVideo}
				/>
			)}

			{model?.supportsReferenceVideo && (model?.maxReferenceVideos ?? 1) > 1 && (!frameRefModes || seedanceMode === 'references') && !hideVideoSlot && (
				<>
					<MultiVideoRow
						videos={referenceVideos}
						// «Переделать видео» (Seedance 2.5) работает с одним роликом: лишние слоты не
						// предлагаем, а если роликов уже несколько — подпись-блокер до запроса.
						maxVideos={frameRefModes && isSeedance25Model(model) && videoIntentOf(settings) === 'edit' ? 1 : (model?.maxReferenceVideos ?? 3)}
						note={videoIntentProblem(model, settings, referenceVideos) ?? undefined}
						maxTotalSeconds={model?.maxReferenceVideoSeconds}
						accept={acceptFor(model?.referenceVideoFormats) ?? undefined}
						isLoading={isLoading}
						isUploading={isUploading}
						inputRef={refVideosInputRef}
						onUpload={handleReferenceVideosUpload}
						onRemove={(idx) =>
							setReferenceVideos((prev) => prev.filter((_, i) => i !== idx))
						}
						tooltip={hint('reference_videos')}
						onShowPicker={pickerRefVideos}
						slotSkeletons={slotSkeletons}
					/>
					{/* TEMP: скрыт инпут скачивания видео по URL (отладка Seedance) */}
				</>
			)}

			{model?.supportsReferenceAudio && (!frameRefModes || seedanceMode !== 'frames') && !hideAudioSlot && (
				<MultiAudioRow
					audios={referenceAudios}
					maxAudios={model?.maxReferenceAudios ?? 3}
					maxTotalSeconds={model?.maxReferenceAudioSeconds}
					accept={acceptFor(model?.referenceAudioFormats) ?? undefined}
					isLoading={isLoading}
					isUploading={isUploading}
					inputRef={refAudiosInputRef}
					onUpload={handleReferenceAudiosUpload}
					onRemove={(idx) =>
						setReferenceAudios((prev) => prev.filter((_, i) => i !== idx))
					}
					tooltip={hint('reference_audios')}
					onShowPicker={pickerRefAudios}
					slotSkeletons={slotSkeletons}
				/>
			)}

			{model.supportsReferenceImages && (
				<MultiImgRow
					images={omniReferenceImages}
					maxImages={referenceVideo ? 4 : 7}
					isLoading={isLoading}
					isUploading={isUploading}
					inputRef={refImagesInputRef}
					onUpload={handleOmniRefImageUpload}
					onRemove={(idx) =>
						setOmniReferenceImages((prev) => prev.filter((_, i) => i !== idx))
					}
					tooltip={hint('reference_images')}
					onShowPicker={pickerRefImages}
					slotSkeletons={slotSkeletons}
				/>
			)}
			</>
			)}

			{/* Капсула-ТРАНСФОРМЕР (решение владельца 2026-08-01): один элемент
			    на оба состояния и в одном месте — до выбора силуэт + «Персонаж»
			    + NEW, после выбора ТОТ ЖЕ кружок заполняется лицом, текст
			    становится именем, справа появляется «×». Прежняя схема
			    «вход в ряду / статус в шапке» подменяла кнопку и переносила её
			    между блоками. */}
			{/* Капсула «Персонаж NEW» рядом со слотами УБРАНА (владелец 06.09): вход —
			    пункт «Персонаж» в меню фото-слота (UploadPickMenu, там же бейдж NEW),
			    статус — фото персонажа в самом слоте, снятие — «×» на этом фото
			    (страж в useSceneApply снимает ассет и его липкость). Хост пикера ниже
			    остаётся: событие меню ловит этот экземпляр; «Сделать персонажем» на фото
			    слота открывает общее окно «Новый персонаж» (хост в App). */}
			<ScenePickerHost probe={probePlan} onConfirm={confirmScene} />

			{appendSlot}

			<AudioTrimSheet
				file={audioTrimFile}
				maxSeconds={model?.maxReferenceAudioSeconds}
				onApply={applyTrimmedAudio}
				onCancel={cancelAudioTrim}
			/>
			<VideoTrimSheet
				videoUrl={videoTrimUrl}
				maxSeconds={model?.maxReferenceVideoSeconds}
				onApply={applyTrimmedVideo}
				onCancel={cancelVideoTrim}
			/>
		</>
	)
}
